import React, { useState, useEffect } from 'react';
import { userTypeAPI, planAPI, branchAPI } from '../../services/api';
import { useBranchStore } from '../../store';
import { toast } from 'react-toastify';
import './UserTypes.css';

const DEFAULT_FORM = {
    name: '',
    code: '',
    description: '',
    branchId: '',
    defaultPlanId: '',
    priority: 0,
    isActive: true,
    defaults: {
        downloadSpeed: '',
        uploadSpeed: '',
        sessionTime: '',
        dataLimit: '',
        deviceLimit: 1,
        authRequired: true,
        autoLogin: false,
        price: 0,
        expiry: { value: '', unit: 'hours' },
    },
};

const UserTypes = () => {
    const { selectedBranch } = useBranchStore();
    const [userTypes, setUserTypes] = useState([]);
    const [plans, setPlans] = useState([]);
    const [branches, setBranches] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editing, setEditing] = useState(null);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [toDelete, setToDelete] = useState(null);
    const [formData, setFormData] = useState(DEFAULT_FORM);

    useEffect(() => {
        fetchAll();
    }, [selectedBranch]);

    const fetchAll = async () => {
        try {
            setLoading(true);
            const branchId = selectedBranch ? selectedBranch._id : undefined;
            const [utRes, planRes, branchRes] = await Promise.all([
                userTypeAPI.getAll(branchId ? { branchId } : undefined),
                planAPI.getAll(branchId),
                branchAPI.getAll(),
            ]);
            setUserTypes(utRes.data.data || []);
            setPlans(planRes.data.data.plans || []);
            setBranches(branchRes.data.data.branches || []);
        } catch (error) {
            toast.error('Failed to fetch user types');
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        const val = type === 'checkbox' ? checked : value;
        if (name.startsWith('defaults.')) {
            const key = name.split('.')[1];
            setFormData(prev => ({ ...prev, defaults: { ...prev.defaults, [key]: val } }));
        } else if (name === 'expiryValue' || name === 'expiryUnit') {
            setFormData(prev => ({
                ...prev,
                defaults: {
                    ...prev.defaults,
                    expiry: { ...prev.defaults.expiry, [name === 'expiryValue' ? 'value' : 'unit']: val },
                },
            }));
        } else {
            setFormData(prev => ({ ...prev, [name]: val }));
        }
    };

    const resetForm = () => setFormData({ ...DEFAULT_FORM, branchId: selectedBranch?._id || '' });

    const handleNew = () => {
        setEditing(null);
        resetForm();
        setShowModal(true);
    };

    const handleEdit = (ut) => {
        setEditing(ut);
        setFormData({
            name: ut.name,
            code: ut.code || '',
            description: ut.description || '',
            branchId: ut.branchId?._id || ut.branchId || '',
            defaultPlanId: ut.defaultPlanId?._id || ut.defaultPlanId || '',
            priority: ut.priority || 0,
            isActive: ut.isActive !== false,
            defaults: {
                downloadSpeed: ut.defaults?.downloadSpeed || '',
                uploadSpeed: ut.defaults?.uploadSpeed || '',
                sessionTime: ut.defaults?.sessionTime || '',
                dataLimit: ut.defaults?.dataLimit || '',
                deviceLimit: ut.defaults?.deviceLimit ?? 1,
                authRequired: ut.defaults?.authRequired !== false,
                autoLogin: !!ut.defaults?.autoLogin,
                price: ut.defaults?.price || 0,
                expiry: { value: ut.defaults?.expiry?.value || '', unit: ut.defaults?.expiry?.unit || 'hours' },
            },
        });
        setShowModal(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.name.trim()) return toast.warning('Name is required');
        try {
            if (editing) {
                await userTypeAPI.update(editing._id, formData);
                toast.success('User type updated');
            } else {
                await userTypeAPI.create(formData);
                toast.success('User type created');
            }
            setShowModal(false);
            setEditing(null);
            resetForm();
            fetchAll();
        } catch (error) {
            toast.error(error.response?.data?.message || 'Operation failed');
        }
    };

    const handleDelete = (ut) => {
        setToDelete(ut);
        setShowDeleteModal(true);
    };

    const confirmDelete = async () => {
        if (!toDelete) return;
        try {
            await userTypeAPI.delete(toDelete._id);
            toast.success('User type deleted');
            fetchAll();
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to delete');
        } finally {
            setShowDeleteModal(false);
            setToDelete(null);
        }
    };

    const handleReapply = async (ut) => {
        try {
            const res = await userTypeAPI.reapply(ut._id);
            toast.success(res.data.message);
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to reapply');
        }
    };

    return (
        <div className="page-container-modern">
            <div className="page-header-modern">
                <div className="header-content">
                    <div className="header-text">
                        <h1 className="page-title-modern">
                            <i className="fas fa-users-cog"></i>
                            User Types
                        </h1>
                        <p className="page-subtitle-modern">Define WiFi guest categories (Staff, VIP, Corporate, Contractor...) with default plans and limits, auto-applied on user creation</p>
                    </div>
                    <div className="header-actions">
                        <button className="btn-primary-modern" onClick={handleNew}>
                            <i className="fas fa-plus-circle"></i>
                            Add User Type
                        </button>
                    </div>
                </div>
            </div>

            {loading ? (
                <div className="loading-modern">
                    <div className="spinner-modern"></div>
                    <p>Loading user types...</p>
                </div>
            ) : userTypes.length === 0 ? (
                <div className="empty-state-modern">
                    <div className="empty-icon"><i className="fas fa-users-cog"></i></div>
                    <h3>No User Types Yet</h3>
                    <p>Create types like VIP, Corporate, Contractor with their own default plan and limits.</p>
                </div>
            ) : (
                <div className="resource-grid-modern">
                    {userTypes.map(ut => (
                        <div key={ut._id} className="resource-card-modern">
                            <div className="card-header">
                                <div className="resource-avatar" style={{ background: '#a855f715', color: '#a855f7' }}>
                                    <i className="fas fa-id-badge"></i>
                                </div>
                                <div className="resource-title">
                                    <h3>{ut.name}</h3>
                                    <span className="resource-subtitle">{ut.branchId?.name || 'All Branches'}</span>
                                </div>
                                <div className="card-actions">
                                    <button className="action-btn-icon edit" onClick={() => handleEdit(ut)} title="Edit">
                                        <i className="fas fa-edit"></i>
                                    </button>
                                    <button className="action-btn-icon delete" onClick={() => handleDelete(ut)} title="Delete">
                                        <i className="fas fa-trash"></i>
                                    </button>
                                </div>
                            </div>
                            <div className="card-body">
                                <div className="status-badges">
                                    <span className={`status-badge ${ut.isActive ? 'active' : 'inactive'}`}>
                                        <span className="badge-dot"></span>
                                        {ut.isActive ? 'Active' : 'Inactive'}
                                    </span>
                                    {ut.defaultPlanId && <span className="status-badge inactive">{ut.defaultPlanId.name}</span>}
                                </div>
                                <div className="info-grid">
                                    <div className="info-item">
                                        <i className="fas fa-tachometer-alt"></i>
                                        <div>
                                            <span className="info-label">Default Speed</span>
                                            <span className="info-value">
                                                {ut.defaults?.downloadSpeed ? `${ut.defaults.downloadSpeed}/${ut.defaults.uploadSpeed || 0} Kbps` : '—'}
                                            </span>
                                        </div>
                                    </div>
                                    <div className="info-item">
                                        <i className="fas fa-laptop"></i>
                                        <div>
                                            <span className="info-label">Device Limit</span>
                                            <span className="info-value">{ut.defaults?.deviceLimit ?? 1}</span>
                                        </div>
                                    </div>
                                    <div className="info-item">
                                        <i className="fas fa-rupee-sign"></i>
                                        <div>
                                            <span className="info-label">Price</span>
                                            <span className="info-value">₹{ut.defaults?.price || 0}</span>
                                        </div>
                                    </div>
                                    <div className="info-item">
                                        <i className="fas fa-sign-in-alt"></i>
                                        <div>
                                            <span className="info-label">Auto Login</span>
                                            <span className="info-value">{ut.defaults?.autoLogin ? 'Yes' : 'No'}</span>
                                        </div>
                                    </div>
                                </div>
                                {ut.defaultPlanId && (
                                    <button className="btn-secondary-modern" style={{ width: '100%', marginTop: '0.75rem' }} onClick={() => handleReapply(ut)}>
                                        <i className="fas fa-sync"></i> Re-apply plan to all users of this type
                                    </button>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {showModal && (
                <div className="modal-overlay-modern" onClick={() => setShowModal(false)}>
                    <div className="modal-modern modal-large" onClick={e => e.stopPropagation()}>
                        <div className="modal-header-modern">
                            <h2>{editing ? 'Edit User Type' : 'Add User Type'}</h2>
                            <button className="modal-close" onClick={() => setShowModal(false)}>
                                <i className="fas fa-times"></i>
                            </button>
                        </div>
                        <form onSubmit={handleSubmit}>
                            <div className="modal-body-modern">
                                <div className="form-grid">
                                    <div className="form-group-modern">
                                        <label>Name <span style={{ color: 'var(--danger-color)' }}>*</span></label>
                                        <input type="text" name="name" value={formData.name} onChange={handleInputChange} placeholder="e.g. VIP Guest, Contractor" required />
                                    </div>
                                    <div className="form-group-modern">
                                        <label>Code</label>
                                        <input type="text" name="code" value={formData.code} onChange={handleInputChange} placeholder="e.g. vip_guest" />
                                    </div>
                                    <div className="form-group-modern full-width">
                                        <label>Description</label>
                                        <input type="text" name="description" value={formData.description} onChange={handleInputChange} placeholder="Optional" />
                                    </div>
                                    <div className="form-group-modern">
                                        <label>Branch</label>
                                        <select name="branchId" value={formData.branchId} onChange={handleInputChange}>
                                            <option value="">All Branches</option>
                                            {branches.map(b => <option key={b._id} value={b._id}>{b.name}</option>)}
                                        </select>
                                    </div>
                                    <div className="form-group-modern">
                                        <label>Default Plan</label>
                                        <select name="defaultPlanId" value={formData.defaultPlanId} onChange={handleInputChange}>
                                            <option value="">None — use fallback defaults below</option>
                                            {plans.map(p => <option key={p._id} value={p._id}>{p.name}</option>)}
                                        </select>
                                        <p className="input-hint">Auto-assigned to any user created with this type, unless a plan is explicitly chosen.</p>
                                    </div>

                                </div>

                                <div className="form-section">
                                    <h3 className="section-title">
                                        <i className="fas fa-sliders-h"></i>
                                        Fallback Defaults (used when no plan resolves)
                                    </h3>
                                    <div className="form-grid">
                                    <div className="form-group-modern">
                                        <label>Download Speed (Kbps)</label>
                                        <input type="number" name="defaults.downloadSpeed" value={formData.defaults.downloadSpeed} onChange={handleInputChange} placeholder="e.g. 5120" />
                                    </div>
                                    <div className="form-group-modern">
                                        <label>Upload Speed (Kbps)</label>
                                        <input type="number" name="defaults.uploadSpeed" value={formData.defaults.uploadSpeed} onChange={handleInputChange} placeholder="e.g. 2048" />
                                    </div>
                                    <div className="form-group-modern">
                                        <label>Session Time (minutes)</label>
                                        <input type="number" name="defaults.sessionTime" value={formData.defaults.sessionTime} onChange={handleInputChange} placeholder="0 = unlimited" />
                                    </div>
                                    <div className="form-group-modern">
                                        <label>Data Limit (MB)</label>
                                        <input type="number" name="defaults.dataLimit" value={formData.defaults.dataLimit} onChange={handleInputChange} placeholder="0 = unlimited" />
                                    </div>
                                    <div className="form-group-modern">
                                        <label>Device Limit</label>
                                        <input type="number" min="1" name="defaults.deviceLimit" value={formData.defaults.deviceLimit} onChange={handleInputChange} />
                                    </div>
                                    <div className="form-group-modern">
                                        <label>Price (₹)</label>
                                        <input type="number" min="0" name="defaults.price" value={formData.defaults.price} onChange={handleInputChange} />
                                    </div>
                                    <div className="form-group-modern">
                                        <label>Expiry</label>
                                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                                            <input type="number" name="expiryValue" value={formData.defaults.expiry.value} onChange={handleInputChange} placeholder="Value" />
                                            <select name="expiryUnit" value={formData.defaults.expiry.unit} onChange={handleInputChange}>
                                                <option value="minutes">Minutes</option>
                                                <option value="hours">Hours</option>
                                                <option value="days">Days</option>
                                                <option value="months">Months</option>
                                            </select>
                                        </div>
                                    </div>
                                    <div className="form-group-modern">
                                        <label>Priority</label>
                                        <input type="number" name="priority" value={formData.priority} onChange={handleInputChange} />
                                    </div>
                                    <div className="form-group-modern">
                                        <label className="checkbox-wrapper" style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                                            <input type="checkbox" name="defaults.authRequired" checked={formData.defaults.authRequired} onChange={handleInputChange} />
                                            <span>Authentication Required</span>
                                        </label>
                                    </div>
                                    <div className="form-group-modern">
                                        <label className="checkbox-wrapper" style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                                            <input type="checkbox" name="defaults.autoLogin" checked={formData.defaults.autoLogin} onChange={handleInputChange} />
                                            <span>Auto Login</span>
                                        </label>
                                    </div>
                                    <div className="form-group-modern full-width">
                                        <label className="checkbox-wrapper" style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                                            <input type="checkbox" name="isActive" checked={formData.isActive} onChange={handleInputChange} />
                                            <span>Active</span>
                                        </label>
                                    </div>
                                    </div>
                                </div>
                            </div>
                            <div className="modal-footer-modern">
                                <button type="button" className="btn-secondary-modern" onClick={() => setShowModal(false)}>Cancel</button>
                                <button type="submit" className="btn-primary-modern">
                                    <i className="fas fa-check"></i>
                                    {editing ? 'Update' : 'Create'} User Type
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {showDeleteModal && (
                <div className="modal-overlay-modern" onClick={() => setShowDeleteModal(false)}>
                    <div className="modal-modern modal-small" onClick={e => e.stopPropagation()}>
                        <div className="modal-header-modern danger">
                            <h2>Delete User Type</h2>
                            <button className="modal-close" onClick={() => setShowDeleteModal(false)}>
                                <i className="fas fa-times"></i>
                            </button>
                        </div>
                        <div className="modal-body-modern">
                            <div style={{ padding: '1rem', background: '#fef2f2', borderRadius: '8px', color: '#991b1b' }}>
                                <p>Are you sure you want to delete <strong>{toDelete?.name}</strong>?</p>
                            </div>
                        </div>
                        <div className="modal-footer-modern">
                            <button className="btn-secondary-modern" onClick={() => setShowDeleteModal(false)}>Cancel</button>
                            <button className="btn-danger-modern" onClick={confirmDelete}>
                                <i className="fas fa-trash"></i> Delete
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default UserTypes;
